/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import * as stores from "stores";
import SearchButtonContainer from "containers/search/SearchButtonContainer";
import { SearchWrapperContainer } from "containers/search/SearchWrapperContainer";
import { DownloadButtonContainer } from "containers/topbar/DownloadButtonContainer";
import { PrintButtonContainer } from "print/components/PrintButtonContainer";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import injectStores from "dc-preview-utils/src/Utils/ProviderUtil";
import loadTranslation from "utils/TranslationUtil";
import { isPDFContainerCreator } from "containers/topbar/IsPDFContainerCreator";
import { ShouldShowSearchInHudContainer } from "containers/topbar/ShouldShowSearchInHudContainer";
import { ReadAloudStartContainer } from "containers/readAloud/ReadAloudStartContainer";
import { ReadAloudRatePickerContainer } from "containers/readAloud/ReadAloudRatePickerContainer";
import { ReadAloudPrevContainer } from "containers/readAloud/ReadAloudPrevContainer";
import { ReadAloudPlayPauseContainer } from "containers/readAloud/ReadAloudPlayPauseContainer";
import { ReadAloudNextContainer } from "containers/readAloud/ReadAloudNextContainer";
import { ReadAloudVoicePickerContainer } from "containers/readAloud/ReadAloudVoicePickerContainer";
import { ReadAloudStopContainer } from "containers/readAloud/ReadAloudStopContainer";
import { AssistantButtonContainer } from "containers/assistant/AssistantButtonContainer";
import { FileNameContainer } from "containers/readAloud/FileNameContainer";

const getLocalizedComponentWithInjectedStores = component => {
  const LocalizedComponent = localizeComponent(component, loadTranslation);
  return injectStores(LocalizedComponent, stores);
};

const getSearchButton = ({ isPDF, ...rest }) => {
  const searchContainer = <SearchButtonContainer {...rest} />;
  return isPDF ? (rest.shouldShowSearchInHud ? null : searchContainer) : null;
};

const getSearchContainer = ({ isPDF, ...rest }) => (isPDF ? <SearchWrapperContainer {...rest} /> : null);

/**
 * Note that the following are Components and not instances
 */

export const wrappedSearchButton = getLocalizedComponentWithInjectedStores(isPDFContainerCreator(getSearchButton));
export const wrappedSearchContainer = getLocalizedComponentWithInjectedStores(
  isPDFContainerCreator(getSearchContainer)
);

export const wrappedDownloadButtonContainer = getLocalizedComponentWithInjectedStores(DownloadButtonContainer);
export const wrappedPrintButtonContainer = getLocalizedComponentWithInjectedStores(PrintButtonContainer);
export const wrappedSearchButtonAccountingHUD = getLocalizedComponentWithInjectedStores(
  isPDFContainerCreator(ShouldShowSearchInHudContainer(getSearchButton))
);
export const wrappedReadAloudStartButton = getLocalizedComponentWithInjectedStores(ReadAloudStartContainer);
export const wrappedReadAloudVoicePickerContainer = getLocalizedComponentWithInjectedStores(
  ReadAloudVoicePickerContainer
);
export const wrappedReadAloudPrevContainer = getLocalizedComponentWithInjectedStores(ReadAloudPrevContainer);
export const wrappedReadAloudPlayPauseContainer = getLocalizedComponentWithInjectedStores(ReadAloudPlayPauseContainer);
export const wrappedReadAloudNextContainer = getLocalizedComponentWithInjectedStores(ReadAloudNextContainer);
export const wrappedReadAloudRatePickerContainer = getLocalizedComponentWithInjectedStores(
  ReadAloudRatePickerContainer
);
export const wrappedReadAloudStopButton = getLocalizedComponentWithInjectedStores(ReadAloudStopContainer);
export const wrappedReadAloudFileName = getLocalizedComponentWithInjectedStores(FileNameContainer);

export const wrappedAssistantButton = getLocalizedComponentWithInjectedStores(AssistantButtonContainer);
